import AppHistory from '__common/modules/history';
import { LiteProjectActionTypes } from 'actionsConstants';
import { FETCH_PROJECT } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/saveLoadProjectActions';
import { ROOF_PITCH } from '__editor/panelsEditor/components/tiltedRoof/roofPitch';
import { parseRoofs } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/saveProject';
import { MAP_TYPE, getMapTypeInt } from '__common/constants/map';
import { isNxtHorizon, isSolarMount, isCommercialProduct, isRM10Evolution, isRmGridflex10D, isRmGridflex5Rwdi, isSMAscenderFlush } from '__common/constants/products';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { s5AttachmentPreferenceDict } from 'projectDesign/components/projectConfiguration/utils/s5AttachmentsFilters';
import { TileAttachmentType } from 'projectDesign/components/projectConfiguration/fields/smTileReplacementsOrSolarhooks';
import { RoofSubstrate, RoofMaterialThickness, RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';
import { disableMandatoryMidSupportCondition } from 'projectDesign/components/projectConfiguration/utils/updateModuleDims';
import type { UserProfile } from '../../../../decls/bom/userProfile';
import {
  LITE_BUILDING_HEIGHT,
  LITE_EXPOSURE_CATEGORY,
  LITE_TOPOGRAPHIC_FACTOR,
  getCommercialMandatoryDefaults,
  getLiteRMAdditionalUserInputDefaults,
} from 'v3/dashboard/liteProductDefaults';

// Lite needs a default `attachment_type` per (product, roof_type) because
// the right-panel attachment dropdown is hidden — without a sensible
// default, R_PANEL projects ship attachment_type=Butyl which crashes
// the SM/NXT span calc (sm_flush_span_calculations.py:533 — only
// PM_9000S / PM_ADJUST are recognised for R_PANEL).
//
// Mirrors legacy attachmentType.ts's smAttachmentData / nhAttachmentData:
//   - SHINGLE                 → Butyl (Lite spec override of legacy default)
//   - R_PANEL                 → PM_ADJUST (first option in R_PANEL_ROOF_ATT)
//   - TILE / STANDING_SEAM /
//     OTHERS / GABLE / CONCRETE
//                             → first option of the legacy product list:
//                               NXT_ONLY_ATT[0] = STRONGHOLD_ATT_KIT_COMP
//                               SM_FLUSH_ATT[0] = L_FOOT_ONLY
function liteDefaultAttachmentType(productId: number, roofType: number | null | undefined, railsProductId?: number): number | null {
  const isNxt = isNxtHorizon(productId);
  const isSm = isSolarMount(productId);
  const isSmAscenderShingle = isSm && isSMAscenderFlush(railsProductId) && roofType === RoofType.SHINGLE;
  // RM products in Lite hide the Attachment Inputs UI (see BomPanel.tsx) —
  // default to UNIRAC_FLASHLOC_RM so the persisted attachment_type matches
  // the seeded `allow_mechanical_attachments=YES` defaults applied in
  // getCommercialMandatoryDefaults.
  if (isRM10Evolution(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId)) return AttachmentType.UNIRAC_FLASHLOC_RM;
  if (!isNxt && !isSm) return null;
  if (isSmAscenderShingle) return AttachmentType.L_FOOT_ONLY;
  if (roofType === RoofType.R_PANEL) return AttachmentType.PM_ADJUST;
  if (roofType === RoofType.STANDING_SEAM) return AttachmentType.MINI_1SS;
  if (roofType === RoofType.TILE) return null;
  if (roofType === RoofType.SHINGLE) {
    return isNxt ? AttachmentType.STRONGHOLD_ATT_BUTYL : AttachmentType.SM_BUTYL_DTD;
  }
  // Other roof types — match legacy first-in-list behaviour.
  return isNxt ? AttachmentType.STRONGHOLD_ATT_KIT_COMP : AttachmentType.L_FOOT_ONLY;
}

// Lite hides the substrate dropdown that legacy NxtHorizonConfig.ts:256
// requires whenever attachment_type is FLASHLOC_DUO / STRONGHOLD_ATT_BUTYL /
// SM_BUTYL_DTD on a SHINGLE roof. Without a value, NXT BOM crashes in
// reports/.../nxt_horizon_span_calcs.py:get_max_down (no else clause for the
// (Butyl + Shingle + null substrate) branch → returns None → division by
// None in generate_allowable_spans_combinations). Default to RAFTERS — the
// most-permissive Max_Down value (992 lbf) and the typical residential
// install.
function liteDefaultRoofSubstrate(productId: number, attachmentType: number | null | undefined): number | undefined {
  if (attachmentType === undefined || attachmentType === null) return undefined;
  const isButyl = (
    attachmentType === AttachmentType.STRONGHOLD_ATT_BUTYL ||
    attachmentType === AttachmentType.SM_BUTYL_DTD ||
    attachmentType === AttachmentType.FLASHLOC_DUO
  );
  if (!isButyl) return undefined;
  if (!isNxtHorizon(productId) && !isSolarMount(productId)) return undefined;
  return RoofSubstrate.RAFTERS;
}

export function CREATE_LITE_PROJECT_REQUEST(payload: {
  data: any;
  productId: number;
  productName: string;
}) {
  return {
    type: LiteProjectActionTypes.CREATE_LITE_PROJECT_REQUEST,
    payload,
  };
}

export function CREATE_LITE_PROJECT_SUCCESS(result: { project_id: string }) {
  return {
    type: LiteProjectActionTypes.CREATE_LITE_PROJECT_SUCCESS,
    payload: { projectId: result && result.project_id },
  };
}

export function CREATE_LITE_PROJECT_FAILURE() {
  return { type: LiteProjectActionTypes.CREATE_LITE_PROJECT_FAILURE };
}

// Re-emits the existing FETCH_PROJECT using project_id from the POST response.
// Used inside the create epic's onSuccess array — ObservableAjax invokes each
// entry with the ajax result, which here is `{ project_id }`.
export function FETCH_PROJECT_AFTER_LITE_CREATE(result: { project_id: string }) {
  return FETCH_PROJECT(result && result.project_id);
}

// Navigation side effect, matching REDIRECT_TO_PROJECT in appActions.ts.
export function REDIRECT_TO_LITE_PROJECT(productName: string, result: { project_id: string }) {
  const projectId = result && result.project_id;
  if (productName && projectId) {
    AppHistory.push(`/project/lite/design/${productName}/${projectId}`);
  }
  return { type: LiteProjectActionTypes.CREATE_LITE_PROJECT_SUCCESS };
}

// Auto-save: PUT /api/v1/ub_lite/project/:projectId/. Epic reads the current
// projectEnvConfig + moduleSelector to build the body. Dispatched by
// UBuilderLiteContainer's 5s interval and its componentWillUnmount flush.
//
// regenBom — when true, the autosave epic chains an empty-body BOM POST after
// the PUT succeeds (wiping user_builds) and the saveLiteBom epic then chains
// a FETCH so the right panel reloads a freshly-regenerated BOM. Used by
// attachment-type / s5_or_sci / tile_replacement dropdowns whose change must
// invalidate any prior BOM edits — without this, the chained FETCH would hit
// _load_bom on the backend and return stale parts based on the prior
// attachment family. Defaults to false (regular autosave PUT → GET path).
//
// allowEmptyPanels — when true, the epic skips its "no panels in state →
// BOM_RESET and bail" short-circuit. The workspace's drawingManager.roofs +
// panels.panels slices are cleared on workspace unmount (LOAD_ROOFS_FROM_API
// in UBuilderLiteContainer.componentWillUnmount), so any PUT fired from a
// route that doesn't host the workspace (e.g. CompleteBomPage's
// domestic_content toggle) would compute countLitePanels=0 and wipe the
// BOM. Setting this flag tells the epic to trust the server's panel state
// and proceed with the regen / fetch as if panels exist.
// configOverride — set only by PUTs fired from the full BOM page
// (CompleteBomPage). That route never mounts the workspace, so
// state.projectConfiguration holds only store defaults (productId
// solarmount_2, empty env). Building the full payload from that snapshot
// would overwrite the persisted project with wrong values (notably
// product_id). When configOverride is present the epic sends a minimal
// PUT (?partial=true) carrying ONLY the changed field(s), leaving every other
// persisted field untouched. productId is used only to drive the BOM
// regen/fetch chain (resolved from the page's URL slug); it is not written
// into the request body.
export function UPDATE_LITE_PROJECT_REQUEST(payload: { projectId: string; regenBom?: boolean; allowEmptyPanels?: boolean; configOverride?: { productId: number; fields: { [key: string]: any } } }) {
  return {
    type: LiteProjectActionTypes.UPDATE_LITE_PROJECT_REQUEST,
    payload,
  };
}

export function UPDATE_LITE_PROJECT_SUCCESS(result?: { project_id?: string }) {
  return {
    type: LiteProjectActionTypes.UPDATE_LITE_PROJECT_SUCCESS,
    payload: { lastSaveAt: Date.now(), projectId: result && result.project_id },
  };
}

export function UPDATE_LITE_PROJECT_FAILURE() {
  return { type: LiteProjectActionTypes.UPDATE_LITE_PROJECT_FAILURE };
}

// Fetch BOM for a Lite project. Endpoint is body-less GET; epic dispatches
// the legacy FETCH_BOM_PART_LIST_SUCCESS on response so the data lands in
// the shared `state.bom` slice (same shape as legacy /api/v1/bom/<id>/).
//
// bomSig — optional config signature of the persisted config this BOM was
// regenerated from. Carried through the regen chain (updateLiteProject →
// SAVE_LITE_BOM_REQUEST → here) so fetchLiteBom seeds lastBomSig from the
// config that was actually calculated rather than live state, which may have
// moved ahead if the user edited a field while the regen was in flight.
export function FETCH_LITE_BOM_REQUEST(payload: { projectId: string; productId: string; bomSig?: string }) {
  return {
    type: LiteProjectActionTypes.FETCH_LITE_BOM_REQUEST,
    payload,
  };
}

// User-driven BOM save: POST /api/v1/ub_lite/bom/<projectId>/. Replaces the
// project's UserBuild rows (qty/color overrides) and merges extra_parameters.
// Body is built at epic-time from the latest panel snapshot to avoid stale
// captures while the user types.
//
// chainFetch — when true, the saveLiteBom epic dispatches FETCH_LITE_BOM_REQUEST
// after SAVE_LITE_BOM_SUCCESS, so the right panel reloads BOM from the
// backend. Used by the attachment-type regen flow (post-wipe of user_builds)
// where the next GET must regenerate from project state. Defaults to false
// (regular BOM POST never triggers a re-fetch).
export function SAVE_LITE_BOM_REQUEST(payload: {
  projectId: string;
  productId: string;
  body: { parts_list: any[]; rails_lengths: number[]; extra_parameters: any };
  chainFetch?: boolean;
  bomSig?: string;
}) {
  return {
    type: LiteProjectActionTypes.SAVE_LITE_BOM_REQUEST,
    payload,
  };
}

export function SAVE_LITE_BOM_SUCCESS() {
  return { type: LiteProjectActionTypes.SAVE_LITE_BOM_SUCCESS };
}

export function SAVE_LITE_BOM_FAILURE() {
  return { type: LiteProjectActionTypes.SAVE_LITE_BOM_FAILURE };
}

// Reads the current Redux state and builds the /api/v1/ub_lite/project/:id/
// PUT body. projectEnvConfig already holds most fields (zipcode, wind_*,
// seismic_*, system_*, custom_title, building_height, roof_type, etc.) —
// the one gap is the module_* bundle, which getRMProjectConfigurationData
// & friends strip out of env; we refill it from the moduleSelector slice.
// omitEmptyRoofs — when true, the `roofs` key is dropped from the body if no
// drawing data is present (rather than sending the destructive empty list).
// Only the full BOM page's PUTs set this; workspace autosaves leave it false.
export function buildLiteUpdatePayload(appState: any, omitEmptyRoofs: boolean = false): any {
  const pc = appState.projectConfiguration || {};
  const env = pc.projectEnvConfig || {};
  const productId: number = pc.productId;
  const railsProductId: number = pc.railsProductId;
  const ms = appState.moduleSelector || {};
  const modelData = ms.modelData || {};
  const mfgFromList = Array.isArray(ms.mfgs)
    ? ms.mfgs.find((m: any) => m && m.id === ms.selectedMfgId)
    : undefined;
  const modelFromList = Array.isArray(ms.models)
    ? ms.models.find((m: any) => m && m.id === ms.selectedModelId)
    : undefined;

  const rawSnowLoad = env.snow_load;
  const snowLoad = typeof rawSnowLoad === 'number' ? rawSnowLoad : (isNaN(Number(rawSnowLoad)) ? 0 : Number(rawSnowLoad));
  const rawModuleLength = env.module_length ?? modelData.height;
  const moduleLength = typeof rawModuleLength === 'number' ? rawModuleLength : (isNaN(Number(rawModuleLength)) ? 0 : Number(rawModuleLength));
  const isMidSupportMandatory = isRM10Evolution(productId) && disableMandatoryMidSupportCondition(moduleLength, snowLoad) ? 1 : 0;

  // Per-roof drawing data (coords, panels, marker, leadEdge, pixelPerMeter,
  // roofCoordsPixels, etc.). Reuses legacy parseRoofs so the shape matches
  // the existing UBuilder save format byte-for-byte. Lite is always blank-map.
  // parseRoofs drops roofs with empty panels — we send those as `roofs: []`.
  // PR #5145's converter expects a fully-formed roof (reads pixelPerMeter,
  // coords, panels, etc. on init) and KeyErrors on the minimal-pitch shape;
  // pitch persistence is handled separately via project_configuration.roof_pitch
  // + UBuilderLiteContainer's load-time re-injection into the seeded roof.
  const roofs = appState.drawingManager && appState.drawingManager.roofs || {};
  const buildingCode = env.building_code;
  // Per-roof roofPitch is the source of truth (legacy data model); env-level
  // is dropped on load, so reading from there gives stale '3/12' on every save.
  const firstRoofId = Object.keys(roofs)[0];
  const firstRoof = firstRoofId !== undefined ? (roofs[firstRoofId] || {}) : {};
  const rawFirstRoofPitch = firstRoof.roofPitch;
  const projectRoofPitchInt = ROOF_PITCH[rawFirstRoofPitch]
    ?? (typeof rawFirstRoofPitch === 'number' ? rawFirstRoofPitch : (isNaN(Number(rawFirstRoofPitch)) ? 0 : Number(rawFirstRoofPitch)));
  // Persist the active map type so reopen lands in the same mode.
  // The earlier crash on reopen (legacy convertToPixelPoints reading
  // undefined.lat) is now guarded in latLngToPixels, so satellite saves
  // can round-trip safely.
  const currentMapType = (appState.roofsSelector && appState.roofsSelector.mapType) || MAP_TYPE.BLANK;
  let roofPayload: any[] = parseRoofs(roofs, productId, buildingCode, currentMapType);

  // parseRoofs serialises roofPitch via `Number(roof.roofPitch)` for RM
  // products (saveProject.ts:520) — but Lite stores the value as the legacy
  // label '3/12', so Number('3/12') = NaN → null in JSON. The backend
  // converter then sets ProjectArray.roof_pitch = None, crashing GF BOM
  // calcs (factors_sliding.py reads roof_pitch_angle). Re-derive each
  // roof's pitch as the integer that ROOF_PITCH['3/12'] = 14 maps to.
  // For NXT/SM, sourcePitch is already numeric (degrees from Math.atan),
  // so the typeof check passes it through unchanged.
  if (roofPayload.length > 0) {
    roofPayload = roofPayload.map((parsedRoof: any) => {
      if (!parsedRoof) return parsedRoof;
      const source = parsedRoof.id !== undefined ? roofs[parsedRoof.id] : null;
      const sourcePitch = source && source.roofPitch;
      const pitchInt = typeof sourcePitch === 'number'
        ? sourcePitch
        : (ROOF_PITCH[sourcePitch] ?? (isNaN(Number(sourcePitch)) ? projectRoofPitchInt : Number(sourcePitch)));
      return { ...parsedRoof, roofPitch: pitchInt, liteRoofPitch: typeof env.roof_pitch === 'number' ? env.roof_pitch : undefined };
    });
  }

  const result = {
    project_configuration: {
      ...env,
      product_id: productId,
      productId,
      module_id: ms.selectedMfgId || env.module,
      model_id: ms.selectedModelId || env.model,
      module_manufacturer: env.module_manufacturer || (mfgFromList && mfgFromList.name) || null,
      module_model: env.module_model || (modelFromList && modelFromList.name) || modelData.name || null,
      module_watts: env.module_watts ?? modelData.watts,
      module_width: env.module_width ?? modelData.width,
      module_length: env.module_length ?? modelData.height,
      module_thickness: env.module_thickness ?? modelData.thickness,
      module_weight: env.module_weight ?? modelData.weight,
      // Lite-required backend fields not captured by any UI control; reapply
      // on every auto-save so server state stays consistent across reloads.
      building_height: env.building_height ?? LITE_BUILDING_HEIGHT[productId],
      exposure_category: LITE_EXPOSURE_CATEGORY,
      topographic_factor: env.topographical_factor_kzt ?? LITE_TOPOGRAPHIC_FACTOR,
      // PR #5132 fields. Lite's UI doesn't surface these but the serializer
      // accepts them and (for attachment_config) the view's `data['attachment_config']`
      // KeyErrors if omitted — so always send sensible SM defaults.
      // Backend's roof_pitch is IntegerField(choices=ROOF_PITCH_CHOICE); read
      // from per-roof drawingManager.roofs[0].roofPitch (the legacy source of
      // truth — env-level is a redundant denormalisation the load path drops),
      // convert "3/12"-style label to integer or DRF rejects.
      roof_pitch: (typeof env.roof_pitch === 'number' && env.roof_pitch > 0) ? env.roof_pitch : projectRoofPitchInt,
      // 1=google (satellite), 2=bing, 3=white (blank/canvas).
      map_type: getMapTypeInt(currentMapType),
      tilt: typeof env.tilt === 'number' ? env.tilt : null,
      rafter_spacing_inches: env.rafter_spacing_inches ?? 24,
      rail_direction: env.rail_direction ?? 'EW',
      stagger_attachments: env.stagger_attachments ?? false,
      // attachment_config intentionally not sent — Lite uses
      // stagger_attachments alone. Backend's update view skips the
      // ATTACHMENT_CONFIG_CHOICES_VALUES remap when the key is absent
      // (`if data.get('attachment_config'):`), so the column keeps its
      // model default and existing BOM logic falls back.
      // Lite default for attachment_type per (product, roof_type) — see
      // liteDefaultAttachmentType. Falls back to whatever env carries first.
      attachment_type: env.attachment_type
        ?? liteDefaultAttachmentType(productId, env.roof_type, railsProductId)
        ?? null,
      // Lite default for roof_substrate when the project lands on the Butyl
      // attachment family — see liteDefaultRoofSubstrate. Without this, NXT
      // get_max_down crashes on null substrate.
      roof_substrate: env.roof_substrate ?? liteDefaultRoofSubstrate(
        productId,
        env.attachment_type ?? liteDefaultAttachmentType(productId, env.roof_type, railsProductId),
      ),
      // Conditional attachment fields — pass through from env so the backend
      // serializer persists them. Set via SET_PROJECT_OPTION when the user
      // interacts with the conditional dropdowns in the BOM Attachment section.
      rail_clamp_option: env.rail_clamp_option ?? null,
      material_thickness: env.material_thickness ?? (
        (isNxtHorizon(productId) || isSolarMount(productId)) && env.roof_type === RoofType.STANDING_SEAM
          ? RoofMaterialThickness.GA_22
          : (isNxtHorizon(productId) || isSolarMount(productId)) && env.roof_type === RoofType.R_PANEL
            ? RoofMaterialThickness.GA_24
            : null
      ),
      s5_roof_manufacturer: env.s5_roof_manufacturer ?? null,
      s5_roof_panel: env.s5_roof_panel ?? null,
      s5_roof_material: env.s5_roof_material ?? null,
      s5_clamp: env.s5_clamp ?? null,
      s5_ultimate_load_uplift: env.s5_ultimate_load_uplift ?? null,
      s5_ultimate_load_shear: env.s5_ultimate_load_shear ?? null,
      s5_or_sci: (() => {
        if (!isNxtHorizon(productId) && !isSolarMount(productId)) return env.s5_or_sci ?? null;
        const sciTypes: number[] = [AttachmentType.MINI_1SS, AttachmentType.STAND_2SS, AttachmentType.WIDE_2SS];
        const resolvedAttachmentType = env.attachment_type ?? liteDefaultAttachmentType(productId, env.roof_type, railsProductId);
        return sciTypes.includes(resolvedAttachmentType) ? s5AttachmentPreferenceDict.sci : null;
      })(),
      tile_replacement_or_solarhooks: env.tile_replacement_or_solarhooks ?? (env.roof_type === RoofType.TILE ? TileAttachmentType.TILE_REPLACEMENT : null),
      standoff_height: env.standoff_height ?? null,
      central_support: isRM10Evolution(productId) ? isMidSupportMandatory : (env.central_support ?? 0),
      central_support_choice: isRM10Evolution(productId) ? (isMidSupportMandatory ? 2 : 0) : (env.central_support_choice ?? 0),
    },
    // `roofs` is omitted when omitEmptyRoofs is set AND there's no drawing
    // data. An empty list is destructive on the backend (it wipes all
    // existing roofs/panels), so PUTs fired from a route without the
    // workspace mounted (the full BOM page / CompleteBomPage, where
    // drawingManager.roofs is cleared) must drop the key entirely and leave
    // server panels intact. Workspace autosaves keep sending roofs as before.
    ...((omitEmptyRoofs && roofPayload.length === 0) ? {} : { roofs: roofPayload }),
  } as { project_configuration: any; roofs?: any[] };

  // RM10 EVO mandatory field defaults — backfill on auto-save for projects
  // created before these defaults existed. Only sets fields that are still
  // missing (undefined/null) so user-edited values are never overwritten.
  if (isCommercialProduct(productId) && env.roof_type) {
    const commercialDefaults = getCommercialMandatoryDefaults(productId, env.roof_type);
    const pc = result.project_configuration;
    Object.keys(commercialDefaults).forEach(key => {
      if (pc[key] === undefined || pc[key] === null) {
        pc[key] = commercialDefaults[key];
      }
    });
  }

  // Additional User Inputs defaults — backfill on auto-save for GF/RM
  // projects created before this defaulting existed.
  if (isCommercialProduct(productId)) {
    const pc = result.project_configuration;
    const addlDefaults = getLiteRMAdditionalUserInputDefaults(
      productId,
      pc,
      pc.module_length,
    );
    // These values derive from mutable env inputs (building_height /
    // wind_exposure / zipcode / elevation / mean_recurrence_interval / etc.)
    // editable after creation, so always overwrite the stale `...env` values
    // with the freshly computed ones rather than backfilling only when missing.
    Object.keys(addlDefaults).forEach(key => {
      pc[key] = addlDefaults[key];
    });
  }
  return result;
}

export function SAVE_USER_PROFILE_REQUEST(payload: { profile: UserProfile; projectId?: string }) {
  return {
    type: LiteProjectActionTypes.SAVE_USER_PROFILE_REQUEST,
    payload,
  };
}

export function SAVE_USER_PROFILE_SUCCESS(projectId: string | undefined, result: UserProfile | UserProfile[]) {
  const created = Array.isArray(result) ? result[0] : result;
  return {
    type: LiteProjectActionTypes.SAVE_USER_PROFILE_SUCCESS,
    payload: { profile: created, projectId },
  };
}

export function SAVE_USER_PROFILE_FAILURE() {
  return { type: LiteProjectActionTypes.SAVE_USER_PROFILE_FAILURE };
}

export function UPDATE_USER_PROFILE_REQUEST(payload: { profileId: number; profile: UserProfile }) {
  return {
    type: LiteProjectActionTypes.UPDATE_USER_PROFILE_REQUEST,
    payload,
  };
}

export function UPDATE_USER_PROFILE_SUCCESS(result: UserProfile) {
  return {
    type: LiteProjectActionTypes.UPDATE_USER_PROFILE_SUCCESS,
    payload: { profile: result },
  };
}

export function UPDATE_USER_PROFILE_FAILURE() {
  return { type: LiteProjectActionTypes.UPDATE_USER_PROFILE_FAILURE };
}

export function DELETE_USER_PROFILE_REQUEST(payload: { profileId: number }) {
  return {
    type: LiteProjectActionTypes.DELETE_USER_PROFILE_REQUEST,
    payload,
  };
}

export function DELETE_USER_PROFILE_SUCCESS(profileId: number) {
  return {
    type: LiteProjectActionTypes.DELETE_USER_PROFILE_SUCCESS,
    payload: { profileId },
  };
}

export function DELETE_USER_PROFILE_FAILURE() {
  return { type: LiteProjectActionTypes.DELETE_USER_PROFILE_FAILURE };
}

// GET /api/v1/ub_lite/project/<id>/variables/. The default project fetch
// serializer surfaces only Project model columns — ProjectVariable rows
// (e.g. profile_id) are not in its payload. This action fetches them as a
// flat name → value dict so the design workspace can resolve the project's
// current profile association on load.
export function FETCH_PROJECT_VARIABLES_REQUEST(payload: { projectId: string }) {
  return {
    type: LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_REQUEST,
    payload,
  };
}

export function FETCH_PROJECT_VARIABLES_SUCCESS(result: { [name: string]: string }) {
  return {
    type: LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_SUCCESS,
    payload: { variables: result || {} },
  };
}

export function FETCH_PROJECT_VARIABLES_FAILURE() {
  return { type: LiteProjectActionTypes.FETCH_PROJECT_VARIABLES_FAILURE };
}

export function FETCH_USER_PROFILES_REQUEST() {
  return { type: LiteProjectActionTypes.FETCH_USER_PROFILES_REQUEST };
}

export function FETCH_USER_PROFILES_SUCCESS(result: UserProfile[]) {
  return {
    type: LiteProjectActionTypes.FETCH_USER_PROFILES_SUCCESS,
    payload: { profiles: Array.isArray(result) ? result : [] },
  };
}

export function FETCH_USER_PROFILES_FAILURE() {
  return { type: LiteProjectActionTypes.FETCH_USER_PROFILES_FAILURE };
}

export function buildUserProfilePayload(args: {
  productId: number;
  title: string;
  projectConfig: any;
  systemKey?: string;
  systemCode?: string;
  systemLabel?: string;
  systemType?: string;
  selectedMfgId?: number | null;
  selectedModelId?: number | null;
  modelSpecs?: any;
  selectedRoof?: string | null;
  mfgName?: string;
  modelName?: string;
}): UserProfile {
  const projectConfig = { ...(args.projectConfig || {}) };
  delete projectConfig.custom_title;
  return {
    product: args.productId,
    title: args.title,
    project_config: {
      project_configuration: projectConfig,
      system: {
        key: args.systemKey,
        code: args.systemCode,
        label: args.systemLabel,
        type: args.systemType,
        productId: args.productId,
      },
      module: {
        mfg_id: args.selectedMfgId ?? null,
        model_id: args.selectedModelId ?? null,
        mfg_name: args.mfgName || null,
        model_name: args.modelName || null,
        specs: args.modelSpecs || null,
      },
      roof_type: args.selectedRoof ?? null,
    },
  };
}
